import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import {
  DndContext,
  useSensor,
  useSensors,
  MouseSensor,
  DragOverEvent,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  Active,
} from '@dnd-kit/core';
import { useItemThumbnail } from '../../../hooks';

interface CrossContainerDnDProviderProps {
  children: ReactNode;
  onDragEnd?: (event: DragEndEvent) => void;
  onDragStart?: (event: DragStartEvent) => void;
  onDragOver?: (event: DragOverEvent) => void;
  onDragStartData?: (active: Active) => any[];
}

const StyledDragOverlayContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: white;
  border: 2px solid #2196f3;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: grabbing;
  max-width: 420px;
  min-height: 64px;
`;

const StyledThumbnailContainer = styled.div<{ $itemCount: number }>`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: ${({ $itemCount }) => ($itemCount === 1 ? '40px' : `${40 + (Math.min($itemCount, 3) - 1) * 8}px`)};
  height: ${({ $itemCount }) => ($itemCount === 1 ? '40px' : `${40 + (Math.min($itemCount, 3) - 1) * 8}px`)};
  position: relative;
  flex-shrink: 0;
`;

const StyledDragText = styled.div`
  font-size: 14px;
  font-weight: 500;
  flex: 1;
`;

const DragPreviewThumbnail = ({ item, index }: { item: any; index: number }) => {
  const { thumbnailUrl: queriedThumbnailUrl } = useItemThumbnail({
    itemId: item.assetId || item.originalId || item.id,
    itemType: item.type,
    thumbnailUrl: item.thumbnailUrl,
  });

  const thumbnailUrl = queriedThumbnailUrl || item.thumbnailUrl;

  return (
    <div
      style={{
        position: 'absolute',
        left: `${index * 8}px`,
        top: `${index * 8}px`,
        width: '40px',
        height: '40px',
        borderRadius: '4px',
        border: '2px solid black',
        zIndex: 10 - index,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        overflow: 'hidden',
      }}
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt="Thumbnail"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <div
          style={{
            fontSize: '10px',
            color: '#999',
            textAlign: 'center',
          }}
        >
          ?
        </div>
      )}
    </div>
  );
};

export const CrossContainerDnDProvider = ({
  children,
  onDragEnd,
  onDragStart,
  onDragOver,
  onDragStartData,
}: CrossContainerDnDProviderProps) => {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const [draggedItems, setDraggedItems] = useState<any[] | null>(null);

  const customCollisionDetection = (args: any) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length) {
      const nonDropzone = pointerCollisions.filter((c) => !c.id.toString().includes('dropzone'));
      return nonDropzone.length ? nonDropzone : pointerCollisions;
    }

    const rectCollisions = rectIntersection(args);
    if (rectCollisions.length) {
      const nonDropzone = rectCollisions.filter((c) => !c.id.toString().includes('dropzone'));
      return nonDropzone.length ? nonDropzone : rectCollisions;
    }

    // Don't fall back to closestCenter - if pointer isn't over a drop zone, return no collision
    return [];
  };

  const handleDragStart = (event: DragStartEvent) => {
    let items: any[] = [];
    if (onDragStartData) items = onDragStartData(event.active);
    if (!items || items.length === 0) items = [{ id: event.active.id }];

    setDraggedItems(items);

    onDragStart?.(event);
  };

  const handleDragOver = (event: DragOverEvent) => {
    onDragOver?.(event);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggedItems(null);
    onDragEnd?.(event);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {children}

      <DragOverlay dropAnimation={null}>
        {draggedItems && draggedItems.length > 0 && (
          <StyledDragOverlayContainer>
            <StyledThumbnailContainer $itemCount={draggedItems.length}>
              {draggedItems.slice(0, 3).map((item, index) => (
                <DragPreviewThumbnail key={item.instanceId || `${item.id}-${index}`} item={item} index={index} />
              ))}
            </StyledThumbnailContainer>

            <StyledDragText>
              {draggedItems.length > 1
                ? `Dragging ${draggedItems.length} items...`
                : draggedItems[0].displayName || draggedItems[0].name || 'Dragging...'}
            </StyledDragText>
          </StyledDragOverlayContainer>
        )}
      </DragOverlay>
    </DndContext>
  );
};
